import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { refreshMe } from '../store/authSlice';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PhoneCall, Phone, Loader2 } from 'lucide-react';
import { useT, tr } from '../utils/translate';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

// Tashkilotning "Qo'ng'iroq qilish usuli" sozlamasi (Settings -> ATC): 'atc' |
// 'personal' | 'both'. ATC ulanmagan bo'lsa - sozlamadan qat'i nazar har doim
// shaxsiy telefon (tel:) ishlatiladi, chunki ATC orqali qo'ng'iroq umuman
// imkonsiz bo'ladi.
function useCallMode() {
  const atc = useSelector(s => s.auth.user?.organization?.atc) || {};
  if (!atc.connected) return 'personal';
  return atc.callMode || 'both';
}

// Tashkilot bo'yicha bitta joydan boshqariladigan qo'ng'iroq tugmasi - Contacts,
// Contact/Deal detail, Calls sahifalarining barchasida shu ishlatiladi, aks holda
// har bir joy o'zicha ATC yoki tel: tanlab, urinishlar mos kelmay qolardi.
export default function CallButton({ phone, className, iconClassName = 'w-4 h-4', title: titleProp }) {
  const t = useT();
  const title = titleProp || t('shared.callAction');
  const mode = useCallMode();
  const dispatch = useDispatch();
  const ext = useSelector(s => s.auth.user?.atcExtension) || '';
  const [calling, setCalling] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  if (!phone) return null;

  const btnCls = className || 'p-1.5 rounded-lg text-ink-tertiary hover:text-green-700 hover:bg-green-50 transition-colors';

  // Звонок через софтфон (MicroSIP): страница сама ничего не узнает о звонке — панель ждёт событие АТС по внутреннему номеру
  const announceSoftphone = () => {
    if (ext) window.dispatchEvent(new CustomEvent('miko:call-started', { detail: { phone, ext, via: 'softphone' } }));
  };

  const callViaAtc = async (e) => {
    e?.stopPropagation();
    if (calling) return;
    // Ichki raqam (ext) faqat admin tomonidan Sozlamalar -> Xodimlar bo'limidan biriktiriladi.
    // Bosishdan OLDIN tekshiramiz — so'rovsiz va aniq tushuntirish bilan.
    // Данные на странице могут быть устаревшими (номер назначили уже после входа) — сначала спрашиваем сервер.
    let myExt = ext;
    if (!myExt) {
      setCalling(true);
      try { const me = await dispatch(refreshMe()).unwrap(); myExt = me?.atcExtension || ''; } catch { /* нет сети — решит сервер */ }
      setCalling(false);
      if (!myExt) { toast.error(t('callPanel.noExt')); return; }
    }
    setCalling(true);
    try {
      await axios.post(`${API}/atc/call`, { phone });
      // Holat paneli (OutgoingCallPanel) qolganini ko'rsatadi: MicroSIP'da javob bering → mijozga ulanish → suhbat
      window.dispatchEvent(new CustomEvent('miko:call-started', { detail: { phone, ext: myExt } }));
    } catch (err) {
      const code = err.response?.data?.code;
      const msg = err.response?.data?.message || tr('shared.error');
      if (code === 'no_extension') toast.error(t('callPanel.noExt'));
      else toast.error(code ? msg : t('callPanel.startFailed'));
    } finally {
      setCalling(false);
    }
  };

  if (mode === 'personal') {
    return (
      <a href={`tel:${phone}`} onClick={e => { e.stopPropagation(); announceSoftphone(); }} title={title} className={btnCls}>
        <PhoneCall className={iconClassName} />
      </a>
    );
  }

  if (mode === 'atc') {
    return (
      <button type="button" onClick={callViaAtc} disabled={calling} title={ext ? `${t('callPanel.viaPbx', { ext })}` : t('callPanel.noExt')} className={btnCls}>
        {calling ? <Loader2 className={`${iconClassName} animate-spin`} /> : <PhoneCall className={iconClassName} />}
      </button>
    );
  }

  // both — tanlov popover
  return (
    <div className="relative inline-block" ref={menuRef}>
      <button type="button" onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }} disabled={calling}
        title={title} className={btnCls}>
        {calling ? <Loader2 className={`${iconClassName} animate-spin`} /> : <PhoneCall className={iconClassName} />}
      </button>
      {menuOpen && (
        <div onClick={e => e.stopPropagation()}
          className="absolute right-0 top-full mt-1 w-56 bg-white border border-surface-200 rounded-xl shadow-lg py-1 z-30">
          <button type="button" onClick={e => { setMenuOpen(false); callViaAtc(e); }} title={ext ? t('callPanel.menuHint') : t('callPanel.noExt')}
            className={`w-full flex items-start gap-2 px-3 py-2 text-xs text-left transition-colors ${ext ? 'text-ink hover:bg-surface-50' : 'text-ink-disabled'}`}>
            <Phone className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${ext ? 'text-primary-500' : 'text-ink-disabled'}`} />
            <span>{ext ? t('callPanel.viaPbx', { ext }) : t('callPanel.viaPbxNoExt')}{ext && <span className="block text-xs text-ink-tertiary mt-0.5 leading-snug">{t('callPanel.menuHint')}</span>}</span>
          </button>
          <a href={`tel:${phone}`} onClick={e => { e.stopPropagation(); setMenuOpen(false); announceSoftphone(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-ink hover:bg-surface-50 transition-colors">
            <PhoneCall className="w-3.5 h-3.5 text-emerald-700 shrink-0" /> {' '}{t('loc.callButton.fromPersonalPhone')}
          </a>
        </div>
      )}
    </div>
  );
}
